import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal, ConfirmModal } from './ui'

const CHANGE_TYPE_LABELS = {
  created: 'Created',
  updated: 'Updated',
  deactivated: 'Deactivated',
  reactivated: 'Reactivated',
  rolled_back: 'Rolled Back',
}

const CHANGE_TYPE_COLORS = {
  created: 'bg-green-900/30 text-green-400 border border-green-800/50',
  updated: 'bg-blue-900/30 text-blue-400 border border-blue-800/50',
  deactivated: 'bg-red-900/30 text-red-400 border border-red-800/50',
  reactivated: 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/50',
  rolled_back: 'bg-amber-900/30 text-amber-400 border border-amber-800/50',
}

function formatDateTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Shared version history modal for KB articles and directives.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   entityId: number — the ID of the KB article or directive
 *   entityType: 'knowledge' | 'directive'
 *   api: { history(id), version(versionId), rollback(id, versionId) }
 *   queryKeyPrefix: string — e.g. 'knowledge' or 'directives'
 *   renderContent: (versionData) => JSX — renders the version's content snapshot
 */
export default function VersionHistoryModal({
  isOpen,
  onClose,
  entityId,
  entityType,
  api,
  queryKeyPrefix,
  renderContent,
}) {
  const queryClient = useQueryClient()
  const [expandedId, setExpandedId] = useState(null)
  const [confirmRollback, setConfirmRollback] = useState(null)

  const { data: historyData, isLoading } = useQuery({
    queryKey: [queryKeyPrefix, 'history', entityId],
    queryFn: () => api.history(entityId),
    enabled: isOpen && !!entityId,
  })

  const { data: versionData, isLoading: isLoadingVersion } = useQuery({
    queryKey: [queryKeyPrefix, 'version', expandedId],
    queryFn: () => api.version(expandedId),
    enabled: !!expandedId,
  })

  const rollbackMutation = useMutation({
    mutationFn: ({ versionId }) => api.rollback(entityId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix] })
      setConfirmRollback(null)
      setExpandedId(null)
      onClose()
    },
  })

  const history = historyData?.history || []
  const version = versionData?.version

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Version History"
        size="lg"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-lume-teal border-t-transparent" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-indices-muted">
            <p>No version history available.</p>
            <p className="text-sm mt-1">History is recorded when changes are made.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((entry) => {
              const isExpanded = expandedId === entry.id
              return (
                <div
                  key={entry.id}
                  className="border border-dial-border rounded-xl overflow-hidden"
                >
                  {/* Version header — always visible */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-dial-elevated/50 transition-colors text-left"
                  >
                    <span className="text-lume-teal font-mono font-semibold text-sm min-w-[2.5rem]">
                      v{entry.version}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${CHANGE_TYPE_COLORS[entry.changeType] || 'bg-dial-elevated text-indices-muted'}`}
                    >
                      {CHANGE_TYPE_LABELS[entry.changeType] || entry.changeType}
                    </span>
                    <span className="text-indices-muted text-xs flex-1">
                      {formatDateTime(entry.changedAt)}
                    </span>
                    <svg
                      className={`w-4 h-4 text-indices-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-dial-border p-3 bg-dial-black/50">
                      {entry.changeNote && (
                        <p className="text-xs text-amber-400 mb-2 italic">
                          {entry.changeNote}
                        </p>
                      )}

                      {isLoadingVersion ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-lume-teal border-t-transparent" />
                        </div>
                      ) : version ? (
                        <div className="space-y-3">
                          {renderContent(version)}

                          <button
                            onClick={() => setConfirmRollback(entry)}
                            className="w-full mt-3 py-2 px-4 bg-dial-elevated border border-dial-border text-indices-bright rounded-xl text-sm font-medium hover:border-lume-teal hover:text-lume-teal transition-colors"
                          >
                            Restore this version
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!confirmRollback}
        onClose={() => setConfirmRollback(null)}
        onConfirm={() =>
          rollbackMutation.mutate({ versionId: confirmRollback.id })
        }
        title="Restore Version"
        message={`Restore to version ${confirmRollback?.version}? This will overwrite the current ${entityType} and create a new history entry.`}
        confirmText="Restore"
        variant="warning"
        isLoading={rollbackMutation.isPending}
      />
    </>
  )
}
